import { Link } from "react-router-dom";
import LegalLayout, { Section, Mail } from "@/pages/legal/LegalLayout";

// Every claim on this page should be true of the code. If you add a provider,
// a tracker, or a new kind of stored data, update this page in the same change.
// A test checks that every third-party script host in public/index.html is
// named in the providers list below.

export const PROVIDERS = [
    { name: "Railway", purpose: "Hosts the application servers (United States)." },
    { name: "MongoDB Atlas", purpose: "Stores your account and your writing." },
    { name: "Cloudflare", purpose: "Delivers the website." },
    { name: "Google", purpose: "Sign in with Google (accounts.google.com)." },
    { name: "Anthropic", purpose: "Claude models for AI writing features." },
    { name: "OpenAI", purpose: "GPT models for AI writing features." },
    { name: "Stripe", purpose: "Processes payments made on the web." },
    { name: "Google Play and RevenueCat", purpose: "Process and manage purchases made in the Android app." },
    { name: "Resend", purpose: "Sends account emails: verification and password reset." },
];

export default function Privacy() {
    return (
        <LegalLayout
            testId="privacy-page"
            eyebrow="Legal"
            title="Privacy policy"
            intro="Day Dreamer is a place to write. This policy explains what we collect to run it, who we share it with, and how you delete it. Day Dreamer is operated by H. Tokoki LLC, a South Carolina limited liability company."
        >
            <Section title="What we collect">
                <p><strong className="text-foreground">Your account.</strong> Your email address. If you sign in with Google, your name, profile picture, and a Google account identifier. If you sign in with email, your password — stored only as a one-way bcrypt hash that we cannot read.</p>
                <p><strong className="text-foreground">Your writing.</strong> Novels, chapters, scenes, characters, plot notes, version history, and any covers or backgrounds you upload or generate. Comments left by beta readers on your shared novels.</p>
                <p><strong className="text-foreground">What it takes to run the service.</strong> A count of the AI words you use each month, so we can apply your plan's allowance. Session records, stored hashed, that keep you signed in.</p>
                <p><strong className="text-foreground">Purchases.</strong> Your plan, the amount, the date, and the payment processor's transaction reference. We never receive or store card numbers.</p>
                <p><strong className="text-foreground">Messages you send us.</strong> Reports about AI output and support requests.</p>
            </Section>

            <Section title="What we don't do">
                <p>No analytics, advertising trackers, or session recording. We don't sell your data, and we don't use your writing to train AI models.</p>
            </Section>

            <Section title="How AI features use your writing">
                <p>When you use an AI feature, the text that request needs — for example the passage you selected, along with relevant chapter, character, and plot context — is sent to the AI provider that powers that feature: Anthropic, OpenAI, or Google. It is sent only to produce the result you asked for.</p>
            </Section>

            <Section title="Who we share it with">
                <p>Only the service providers that run Day Dreamer for us, and only what each one needs:</p>
                <ul className="list-disc pl-5 space-y-1.5">
                    {PROVIDERS.map((p) => (
                        <li key={p.name}><strong className="text-foreground">{p.name}</strong> — {p.purpose}</li>
                    ))}
                </ul>
                <p>We may also disclose information when the law requires it.</p>
            </Section>

            <Section title="Cookies">
                <p>One strictly necessary cookie keeps you signed in. Google may set its own cookies when you use Sign in with Google.</p>
            </Section>

            <Section title="Sharing your work">
                <p>A beta-reader link lets anyone who has it read that novel and leave comments. You can revoke a link at any time, which closes it immediately.</p>
            </Section>

            <Section title="Keeping and deleting your data">
                <p>You can delete your account from Settings. Your account and writing are hidden at once and permanently deleted after 30 days. Signing back in within those 30 days restores everything.</p>
                <p>After 30 days we delete your account and all of its data from our systems — writing, uploads, usage records, and our copy of your purchase records. Stripe and Google keep their own transaction records as the law requires.</p>
                <p><strong className="text-foreground">Deleting your account does not cancel a Google Play subscription.</strong> Cancel it in the Google Play app as well.</p>
                <p>See <Link to="/delete-account" className="text-primary underline underline-offset-2">how to delete your account</Link>, including without signing in.</p>
            </Section>

            <Section title="Your choices">
                <p>You can view and change your account details in Settings, and export your writing from the web app as .docx, .epub, or .pdf. To ask for a copy of your data, a correction, or deletion, write to <Mail />.</p>
            </Section>

            <Section title="Security">
                <p>Passwords are stored as bcrypt hashes and sign-in sessions as hashed tokens. All traffic is encrypted in transit over HTTPS.</p>
            </Section>

            <Section title="Children">
                <p>Day Dreamer is not directed at children under 13, and we don't knowingly collect their information. If you believe a child has given us information, contact us and we will delete it.</p>
            </Section>

            <Section title="Changes">
                <p>If we change this policy we will update the date above, and tell you in the app before significant changes take effect.</p>
            </Section>

            <Section title="Contact">
                <p>Questions about privacy: <Mail /></p>
            </Section>
        </LegalLayout>
    );
}
